import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, Bug, ArrowRight } from "lucide-react";

const TYPE_META = {
  new: {
    label: "New",
    Icon: Sparkles,
    color: "text-[color:var(--brand-primary)]",
    bg: "bg-[color:rgba(27,168,159,0.10)]",
    border: "border-[color:rgba(27,168,159,0.35)]",
  },
  improved: {
    label: "Improved",
    Icon: TrendingUp,
    color: "text-[color:var(--accent-warm)]",
    bg: "bg-[color:rgba(255,138,61,0.10)]",
    border: "border-[color:rgba(255,138,61,0.35)]",
  },
  fixed: {
    label: "Fixed",
    Icon: Bug,
    color: "text-[color:var(--text-2)]",
    bg: "bg-[color:var(--surface-2)]",
    border: "border-[color:var(--border-subtle)]",
  },
};

function ItemRow({ item }) {
  const meta = TYPE_META[item.type] || TYPE_META.new;
  const Icon = meta.Icon;
  return (
    <li
      className="flex items-start gap-3"
      data-testid={`whats-new-item-${item.type}`}
    >
      <span
        className={`inline-flex items-center gap-1 rounded-full border ${meta.border} ${meta.bg} px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold ${meta.color} shrink-0 mt-0.5`}
      >
        <Icon className="h-3 w-3" />
        {meta.label}
      </span>
      <span className="text-sm text-[color:var(--text-2)] leading-relaxed">
        {item.text}
      </span>
    </li>
  );
}

export default function WhatsNewTimeline({ releases }) {
  if (!releases?.length) {
    return (
      <div
        className="text-sm text-[color:var(--text-3)]"
        data-testid="whats-new-empty"
      >
        No release notes yet — check back after the next update.
      </div>
    );
  }
  return (
    <div className="space-y-8" data-testid="whats-new-timeline">
      {releases.map((release, index) => (
        <article
          key={release.id}
          className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 sm:p-6"
          data-testid={`whats-new-release-${release.id}`}
        >
          <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
              {release.date}
            </div>
            {index === 0 ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.10)] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.18em] text-[color:var(--brand-primary)] font-semibold"
                data-testid="whats-new-latest-badge"
              >
                Latest
              </span>
            ) : null}
          </header>
          <h3 className="font-display text-xl sm:text-2xl font-semibold text-[color:var(--text-1)] tracking-tight mb-4">
            {release.title}
          </h3>
          <ul className="space-y-3">
            {release.items.map((item, i) => (
              <ItemRow key={`${release.id}-${i}`} item={item} />
            ))}
          </ul>
          {release.cta?.to && release.cta?.label ? (
            <div className="mt-5">
              <Link
                to={release.cta.to}
                data-testid={release.cta.testid || `whats-new-cta-${release.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.10)] px-3 py-1.5 text-sm font-semibold text-[color:var(--brand-primary)] hover:bg-[color:rgba(27,168,159,0.18)] transition-colors"
              >
                {release.cta.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
}
